import { IncidentTypesEmpty } from "./Constants";
import {
  generateBarChartData,
  generateEventReplyDatagridData,
  generatePieChartData,
  generateStatCardData,
  generateTimelineData,
  generateVideoDataAnalyticsListing
} from "./DashboardHelper";
import { generateDate, generateTime, getFormattedDate, msToTime2, msToTimeForStatCardDuration } from "./DateHelper";

export const wholeDataGenerator = (incidentList = []) => {
  let kittingData = [...incidentList];
  kittingData = kittingData.sort((a, b) => {
    if (a.incidentTime !== b.incidentTime) {
      return a.incidentTime - b.incidentTime;
    } else {
      return a.startFrame - b.startFrame;
    }
  });
  return kittingData;
}

export const generateAnalyticSummaryPageData = ({
  wholeData = [],
  locationSelected = '',
  cameraSelected = '',
  cameraData = [],
  locationsData = [],
  allIncidents = IncidentTypesEmpty,
  selectedIncidents = [],
  searchStartDate = new Date(new Date().getTime() - 3600 * 1000),
  searchEndDate = new Date(new Date().setHours(23, 59, 59, 999)),
  applicationConfig = ''
}) => {
  let tempWholeData = [...wholeData];

  // filter all data by selected location
  if (locationSelected) {
    tempWholeData = tempWholeData.filter(d => d.locationId === locationSelected);
  }

  // filter all data by selected camera
  if (cameraSelected) {
    tempWholeData = tempWholeData.filter(d => d.cameraId === cameraSelected);
  }

  tempWholeData = tempWholeData.filter((data) => allIncidents.apiCallAllEvents.includes(data.incidentType));

  // filter all data by selected incidents
  if (selectedIncidents && selectedIncidents.length) {
    tempWholeData = tempWholeData.filter((data) => selectedIncidents.includes(data.incidentType));
  }

  const barChartData = generateBarChartData(
    tempWholeData,
    allIncidents.barChartEvents,
    searchStartDate,
    searchEndDate,
    applicationConfig
  );

  const statCardData = generateStatCardData(
    tempWholeData,
    allIncidents.statCardsDurationEvents,
    allIncidents.statCardsCountEvents,
    applicationConfig
  );

  const pieChartData = generatePieChartData(
    tempWholeData,
    allIncidents.pieChartEvents,
    applicationConfig
  );

  const timeLineData = generateTimelineData(
    tempWholeData,
    allIncidents.timeLineEvents,
    cameraData,
    applicationConfig
  );

  const videoData = generateVideoDataAnalyticsListing(
    tempWholeData,
    allIncidents.timeLineEvents,
    cameraData,
    locationsData
  );

  const eventReplyData = generateEventReplyDatagridData(
    tempWholeData,
    locationSelected,
    cameraSelected,
    allIncidents.apiCallAllEvents,
    selectedIncidents,
    allIncidents.eventReplayPageEvents,
  );

  // GE Data
  const machineUpTimeinms = tempWholeData.filter(f => f.incidentType === 'Machine Up Ended')
    .reduce((acc, obj) => acc + obj.incidentData.duration, 0);
  const machineUpTime = msToTimeForStatCardDuration(machineUpTimeinms);

  const machineDownTimeinms = tempWholeData.filter(f => f.incidentType === 'Machine Down Ended')
    .reduce((acc, obj) => acc + obj.incidentData.duration, 0);
  const machineDownTime = msToTimeForStatCardDuration(machineDownTimeinms);

  let percentageMachineUp = 0;
  if (machineUpTimeinms + machineDownTimeinms > 0) {
    percentageMachineUp = 100 * machineUpTimeinms / (machineUpTimeinms + machineDownTimeinms);
    percentageMachineUp = percentageMachineUp.toFixed(1);
  }

  let percentageMachineDown = 0;
  if (machineUpTimeinms + machineDownTimeinms > 0) {
    percentageMachineDown = 100 - percentageMachineUp;
    percentageMachineDown = percentageMachineDown.toFixed(1);
  }

  const idleTimeinms = tempWholeData.filter(f => f.incidentType === 'Empty Rack Ended')
    .reduce((acc, obj) => acc + obj.incidentData.duration, 0);
  const idleTime = msToTimeForStatCardDuration(idleTimeinms);

  const rackWithPartsEndedinms = tempWholeData.filter(f => f.incidentType === 'Rack With Parts Ended')
    .reduce((acc, obj) => acc + obj.incidentData.duration, 0);
  const rackWithPartsEndedinstr = msToTimeForStatCardDuration(rackWithPartsEndedinms);

  let percentageMachineUtilization = 0;
  if (rackWithPartsEndedinms + idleTimeinms > 0) {
    percentageMachineUtilization = 100 * rackWithPartsEndedinms / (rackWithPartsEndedinms + idleTimeinms);
    percentageMachineUtilization = percentageMachineUtilization.toFixed(1);
  }

  const cHookWithRackEndedinms = tempWholeData.filter(f => f.incidentType === 'C Hook With Rack Ended')
    .reduce((acc, obj) => acc + obj.incidentData.duration, 0);
  const cHookWithRackEndedinstr = msToTimeForStatCardDuration(cHookWithRackEndedinms);

  const cHookWithoutRackEndedinms = tempWholeData.filter(f => f.incidentType === 'C Hook Without Rack Ended')
    .reduce((acc, obj) => acc + obj.incidentData.duration, 0);
  const cHookWithoutRackEndedinstr = msToTimeForStatCardDuration(cHookWithoutRackEndedinms);

  const changeoverTime = cHookWithoutRackEndedinstr;
  let percentageChangeover = 0;
  if (cHookWithRackEndedinms + cHookWithoutRackEndedinms > 0) {
    percentageChangeover = 100 * cHookWithoutRackEndedinms / (cHookWithRackEndedinms + cHookWithoutRackEndedinms);
    percentageChangeover = percentageChangeover.toFixed(1);
  }

  const unfinishedPartCount = tempWholeData.filter(f => f.incidentType === 'Unfinished Part Detected').length;
  const reworkedPartCount = tempWholeData.filter(f => f.incidentType === 'Rework Part Detected').length;
  const rackWithPartCount = tempWholeData.filter(f => f.incidentType === 'Rack With Parts Detected').length;
  const emptyRackDetectedCount = tempWholeData.filter(f => f.incidentType === 'Empty Rack Detected').length;
  const cHookWithRackCount = tempWholeData.filter(f => f.incidentType === 'C Hook With Rack Detected').length;
  const cHookWithoutRackCount = tempWholeData.filter(f => f.incidentType === 'C Hook Without Rack Detected').length;
  const numberPlateDetectedCount = tempWholeData.filter(f => f.incidentType === 'Number Plate Detected').length;

  let unfinishedPartPercentage = 0;
  if (unfinishedPartCount + reworkedPartCount > 0) {
    unfinishedPartPercentage = 100 * unfinishedPartCount / (unfinishedPartCount + reworkedPartCount);
    unfinishedPartPercentage = unfinishedPartPercentage.toFixed(1);
  }
  let reworkedPartPercentage = (100 - unfinishedPartPercentage).toFixed(1);

  let rackWithPartPercentage = 0;
  if (rackWithPartCount + emptyRackDetectedCount > 0) {
    rackWithPartPercentage = 100 * rackWithPartCount / (rackWithPartCount + emptyRackDetectedCount);
    rackWithPartPercentage = rackWithPartPercentage.toFixed(1);
  }
  let emptyRackPercentage = (100 - rackWithPartPercentage).toFixed(1);

  let cHookWithRackPercentage = 0;
  if (cHookWithRackCount + cHookWithoutRackCount > 0) {
    cHookWithRackPercentage = 100 * cHookWithRackCount / (cHookWithRackCount + cHookWithoutRackCount);
    cHookWithRackPercentage = cHookWithRackPercentage.toFixed(1);
  }
  let cHookWithoutRackPercenatge = (100 - cHookWithRackPercentage).toFixed(1);

  // AHF data
  let boxesPacked = tempWholeData.filter(f => f.incidentType === 'Box Fill OK' ||
    f.incidentType === 'Underfill Detected' ||
    f.incidentType === 'Overfill Detected').length;
  let fillErrors = tempWholeData.filter(f => f.incidentType === 'Underfill Detected' ||
    f.incidentType === 'Overfill Detected').length;
  let boxFillOK = tempWholeData.filter(f => f.incidentType === 'Box Fill OK').length;
  let underFillBoxes = tempWholeData.filter(f => f.incidentType === 'Underfill Detected').length;
  let overFillBoxes = tempWholeData.filter(f => f.incidentType === 'Overfill Detected').length;
  let boxFillOkPercentage = 0;
  let underFillPercentage = 0;
  let overFillPercentage = 0;
  if (boxesPacked > 0) {
    boxFillOkPercentage = (100 * boxFillOK / boxesPacked).toFixed(1);
    underFillPercentage = (100 * underFillBoxes / boxesPacked).toFixed(1);
    overFillPercentage = (100 * overFillBoxes / boxesPacked).toFixed(1);
  }

  return (
    {
      boxesPacked,
      fillErrors,
      boxFillOK,
      underFillBoxes,
      overFillBoxes,
      boxFillOkPercentage,
      underFillPercentage,
      overFillPercentage,
      qualityDistributionData: {
        barChartData: generateBarChartData(
          tempWholeData,
          ['Box Fill OK', 'Underfill Detected', 'Overfill Detected'],
          searchStartDate,
          searchEndDate,
          applicationConfig
        ),
        pieChartData: {
          chartData: boxFillOkPercentage || underFillBoxes || overFillBoxes
            ? [
              ['Events', 'Count', { type: 'string', role: 'tooltip' }],
              ['Box Fill OK', boxFillOK, `Box Fill OK: ${boxFillOK} (${boxFillOkPercentage}%)`],
              ['Underfill Detected', underFillBoxes, `Underfill Detected: ${underFillBoxes} (${underFillPercentage}%)`],
              ['Overfill Detected', overFillBoxes, `Overfill Detected: ${overFillBoxes} (${overFillPercentage}%)`],
            ]
            : [],
          chartColors: ['#00695C', '#B71C1C', '#B71C1C']
        },
        timeLineData: generateTimelineData(
          tempWholeData,
          [
            'Box Fill OK',
            'Underfill Detected',
            'Overfill Detected',
            'Box Fill Started',
            'Box Fill Ended'
          ],
          cameraData,
          applicationConfig
        ),
        videoData: generateVideoDataAnalyticsListing(
          tempWholeData,
          [
            'Box Fill OK',
            'Underfill Detected',
            'Overfill Detected',
            'Box Fill Started',
            'Box Fill Ended'
          ],
          cameraData,
          locationsData
        )
      },


      machineUpTime,
      machineDownTime,
      percentageMachineUp,
      percentageMachineDown,
      idleTime,
      percentageMachineUtilization,
      changeoverTime,
      percentageChangeover,
      unfinishedPartCount,
      reworkedPartCount,
      rackWithPartCount,
      emptyRackDetectedCount,
      cHookWithRackCount,
      cHookWithoutRackCount,
      numberPlateDetectedCount,

      machineUpdownData: {
        barChartData: generateBarChartData(
          tempWholeData,
          ['Machine Up Started', 'Machine Down Started'],
          searchStartDate,
          searchEndDate,
          applicationConfig
        ),
        pieChartData: {
          chartData: percentageMachineUp || percentageMachineDown
            ? [
              ['Events', 'Count', { type: 'string', role: 'tooltip' }],
              ['Machine Up', machineUpTimeinms, `Machine Up Duration: ${machineUpTime} (${percentageMachineUp}%)`],
              ['Machine Down', machineDownTimeinms, `Machine Down Duration: ${machineDownTime} (${percentageMachineDown}%)`],
            ]
            : [],
          chartColors: ['#00695C', '#B71C1C']
        },
        timeLineData: generateTimelineData(
          tempWholeData,
          ['Machine Up Started', 'Machine Down Started', 'Machine Up Ended', 'Machine Down Ended'],
          cameraData,
          applicationConfig
        ),
        videoData: generateVideoDataAnalyticsListing(
          tempWholeData,
          ['Machine Up Started', 'Machine Down Started', 'Machine Up Ended', 'Machine Down Ended'],
          cameraData,
          locationsData
        )
      },

      machineUtilizationData: {
        barChartData: generateBarChartData(
          tempWholeData,
          ['Rack With Parts Started', 'Empty Rack Started'],
          searchStartDate,
          searchEndDate,
          applicationConfig
        ),
        pieChartData: {
          chartData: rackWithPartsEndedinms || idleTimeinms
            ? [
              ['Events', 'Count', { type: 'string', role: 'tooltip' }],
              ['Empty Rack', idleTimeinms, `Empty Rack: ${idleTime} (${(100 - percentageMachineUtilization).toFixed(1)}%)`],
              ['Rack With Parts', rackWithPartsEndedinms, `Rack With Parts: ${rackWithPartsEndedinstr} (${percentageMachineUtilization}%)`],
            ]
            : [],
          chartColors: ['#FFAB40', '#1F363D']
        },
        timeLineData: generateTimelineData(
          tempWholeData,
          ['Rack With Parts Started',
            'Rack With Parts Ended',
            'Empty Rack Started',
            'Empty Rack Ended'],
          cameraData,
          applicationConfig
        ),
        videoData: generateVideoDataAnalyticsListing(
          tempWholeData,
          ['Rack With Parts Started',
            'Rack With Parts Ended',
            'Empty Rack Started',
            'Empty Rack Ended'],
          cameraData,
          locationsData
        )
      },

      changeoverData: {
        barChartData: generateBarChartData(
          tempWholeData,
          ['C Hook With Rack Started', 'C Hook Without Rack Started'],
          searchStartDate,
          searchEndDate,
          applicationConfig
        ),
        pieChartData: {
          chartData: cHookWithRackEndedinms || cHookWithoutRackEndedinms
            ? [
              ['Events', 'Count', { type: 'string', role: 'tooltip' }],
              ['C Hook without Rack', cHookWithoutRackEndedinms, `C Hook without Rack: ${cHookWithoutRackEndedinstr} (${percentageChangeover}%)`],
              ['C Hook with Rack', cHookWithRackEndedinms, `C Hook with Rack: ${cHookWithRackEndedinstr} (${(100 - percentageChangeover).toFixed(1)}%)`],
            ]
            : [],
          chartColors: ['#FFAB40', '#1F363D']
        },
        timeLineData: generateTimelineData(
          tempWholeData,
          ['C Hook With Rack Started',
            'C Hook With Rack Ended',
            'C Hook Without Rack Started',
            'C Hook Without Rack Ended'],
          cameraData,
          applicationConfig
        ),
        videoData: generateVideoDataAnalyticsListing(
          tempWholeData,
          ['C Hook With Rack Started',
            'C Hook With Rack Ended',
            'C Hook Without Rack Started',
            'C Hook Without Rack Ended'],
          cameraData,
          locationsData
        )
      },

      partsData: {
        barChartData: generateBarChartData(
          tempWholeData,
          ['Unfinished Part Detected', 'Rework Part Detected'],
          searchStartDate,
          searchEndDate,
          applicationConfig
        ),
        pieChartData: {
          chartData: unfinishedPartCount || reworkedPartCount
            ? [
              ['Events', 'Count', { type: 'string', role: 'tooltip' }],
              ['Unfinished Parts', unfinishedPartCount, `Unfinished Parts: ${unfinishedPartCount} (${unfinishedPartPercentage}%)`],
              ['Reworked Parts', reworkedPartCount, `Reworked Parts: ${reworkedPartCount} (${reworkedPartPercentage}%)`],
            ]
            : [],
          chartColors: ['#1F363D', '#FFAB40']
        },
        timeLineData: generateTimelineData(
          tempWholeData,
          ['Unfinished Part Detected', 'Rework Part Detected'],
          cameraData,
          applicationConfig
        ),
        videoData: generateVideoDataAnalyticsListing(
          tempWholeData,
          ['Unfinished Part Detected', 'Rework Part Detected'],
          cameraData,
          locationsData
        )
      },

      racksData: {
        barChartData: generateBarChartData(
          tempWholeData,
          ['Rack With Parts Detected', 'Empty Rack Detected'],
          searchStartDate,
          searchEndDate,
          applicationConfig
        ),
        pieChartData: {
          chartData: rackWithPartCount || emptyRackDetectedCount
            ? [
              ['Events', 'Count', { type: 'string', role: 'tooltip' }],
              ['Rack With Parts', rackWithPartCount, `Rack With Parts: ${rackWithPartCount} (${rackWithPartPercentage}%)`],
              ['Empty Racks', emptyRackDetectedCount, `Empty Racks: ${emptyRackDetectedCount} (${emptyRackPercentage}%)`],
            ]
            : [],
          chartColors: ['#1F363D', '#FFAB40']
        },
        timeLineData: generateTimelineData(
          tempWholeData,
          ['Rack With Parts Detected', 'Empty Rack Detected'],
          cameraData,
          applicationConfig
        ),
        videoData: generateVideoDataAnalyticsListing(
          tempWholeData,
          ['Rack With Parts Detected', 'Empty Rack Detected'],
          cameraData,
          locationsData
        )
      },

      cHookData: {
        barChartData: generateBarChartData(
          tempWholeData,
          ['C Hook With Rack Detected', 'C Hook Without Rack Detected'],
          searchStartDate,
          searchEndDate,
          applicationConfig
        ),
        pieChartData: {
          chartData: cHookWithRackCount || cHookWithoutRackCount
            ? [
              ['Events', 'Count', { type: 'string', role: 'tooltip' }],
              ['C Hook With Rack', cHookWithRackCount, `C Hook With Rack: ${cHookWithRackCount} (${cHookWithRackPercentage}%)`],
              ['C Hook Wothout Rack', cHookWithoutRackCount, `C Hook Wothout Rack: ${cHookWithoutRackCount} (${cHookWithoutRackPercenatge}%)`],
            ]
            : [],
          chartColors: ['#1F363D', '#FFAB40']
        },
        timeLineData: generateTimelineData(
          tempWholeData,
          ['C Hook With Rack Detected', 'C Hook Without Rack Detected'],
          cameraData,
          applicationConfig
        ),
        videoData: generateVideoDataAnalyticsListing(
          tempWholeData,
          ['C Hook With Rack Detected', 'C Hook Without Rack Detected'],
          cameraData,
          locationsData
        )
      },

      barChartData,
      statCardData,
      pieChartData,
      timeLineData,
      videoData,
      eventReplyData
    }
  );
}

